import { lazy, Suspense } from 'react';
import * as Sentry from '@sentry/react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { RedirectToSignIn } from '@clerk/react';
import { useAuth as useClerkAuth } from '@clerk/react';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/context/AuthContext';
import { TenantProvider, useTenant } from '@/context/TenantContext';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { SkipNavigation } from '@/components/shared/SkipNavigation';
import { RouteAnnouncer } from '@/components/shared/RouteAnnouncer';
import { FullPageLoader } from '@/components/shared/LoadingSpinner';
import { UpgradePrompt } from '@/features/billing/components/UpgradePrompt';
import {
  TemplateBuilderPage,
  ChecklistDashboardPage,
  MyChecklistsPage,
} from '@/features/checklists';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { WifiOff, AlertTriangle } from 'lucide-react';
import DashboardLayout from '@/layouts/DashboardLayouts';
import ComplianceItemTypesPage from './features/compliance/pages/ComplianceItemTypesPage';

// ── Lazy-loaded pages ────────────────────────────────
const SignInPage = lazy(() => import('@/pages/SignInPage'));
const SignUpPage = lazy(() => import('@/pages/SignUpPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));

// Module 1: Documents
const DocumentListPage = lazy(() =>
  import('@/features/documents/pages/DocumentListPage').then((m) => ({ default: m.DocumentListPage }))
);
const DocumentDetailPage = lazy(() =>
  import('@/features/documents/pages/DocumentDetailPage').then((m) => ({ default: m.DocumentDetailPage }))
);
const DocumentDashboardPage = lazy(() =>
  import('@/features/documents/pages/DocumentDashboardPage').then((m) => ({ default: m.DocumentDashboardPage }))
);
const CreateDocumentPage = lazy(() =>
  import('@/features/documents/pages/CreateDocumentPage').then((m) => ({ default: m.CreateDocumentPage }))
);
const RenewDocumentPage = lazy(() =>
  import('@/features/documents/pages/RenewDocumentPage')
);

// Module 2: Checklists
const ChecklistListPage = lazy(() =>
  import('@/features/checklists/pages/ChecklistListPage').then((m) => ({ default: m.ChecklistListPage }))
);
const ChecklistDetailPage = lazy(() =>
  import('@/features/checklists/pages/ChecklistDetailPage').then((m) => ({ default: m.ChecklistDetailPage }))
);
const TemplateListPage = lazy(() =>
  import('@/features/checklists/pages/TemplateListPage').then((m) => ({ default: m.TemplateListPage }))
);

// Module 3: Policies
const PolicyListPage = lazy(() =>
  import('@/features/policies/pages/PolicyListPage').then((m) => ({ default: m.PolicyListPage }))
);
const PolicyReaderPage = lazy(() =>
  import('@/features/policies/pages/PolicyReaderPage').then((m) => ({ default: m.PolicyReaderPage }))
);
const PolicyEditorPage = lazy(() =>
  import('@/features/policies/pages/PolicyEditorPage').then((m) => ({ default: m.PolicyEditorPage }))
);
const ComplianceDashboardPage = lazy(() =>
  import('@/features/policies/pages/ComplianceDashboardPage').then((m) => ({ default: m.ComplianceDashboardPage }))
);
const EmployeePoliciesPage = lazy(() =>
  import('@/features/policies/pages/EmployeePoliciesPage').then((m) => ({ default: m.EmployeePoliciesPage }))
);

// Module 4: Training
const CourseListPage = lazy(() =>
  import('@/features/training/pages/CourseListPage').then((m) => ({ default: m.CourseListPage }))
);
const CourseBuilderPage = lazy(() =>
  import('@/features/training/pages/CourseBuilderPage').then((m) => ({ default: m.CourseBuilderPage }))
);
const CourseLearnerPage = lazy(() =>
  import('@/features/training/pages/CourseLearnerPage').then((m) => ({ default: m.CourseLearnerPage }))
);
const MyCertificationsPage = lazy(() =>
  import('@/features/training/pages/MyCertificationsPage').then((m) => ({ default: m.MyCertificationsPage }))
);
const TrainingDashboardPage = lazy(() =>
  import('@/features/training/pages/TrainingDashboardPage').then((m) => ({ default: m.TrainingDashboardPage }))
);
const CourseDetailPage = lazy(() =>
  import('@/features/training/pages/CourseDetailPage').then((m) => ({ default: m.CourseDetailPage }))
);

// Module 5: Access
const SystemRegistryPage = lazy(() => import('@/features/access/pages/SystemRegistryPage'));
const AccessDashboardPage = lazy(() => import('@/features/access/pages/AccessDashboardPage'));
const AccessRequestPage = lazy(() => import('@/features/access/pages/AccessRequestPage'));
const OffboardingDashboardPage = lazy(() => import('@/features/access/pages/OffboardingDashboardPage'));
const RoleTemplateEditorPage = lazy(() => import('@/features/access/pages/RoleTemplateEditorPage'));

// Module 6: Compliance
const EmployeeListPage = lazy(() =>
  import('@/features/compliance/pages/EmployeeListPage').then((m: any) => ({ default: m.EmployeeListPage ?? m.default }))
);
const EmployeeProfilePage = lazy(() =>
  import('@/features/compliance/pages/EmployeeProfilePage').then((m: any) => ({ default: m.EmployeeProfilePage ?? m.default }))
);
const ComplianceHubPage = lazy(() =>
  import('@/features/compliance/pages/ComplianceHubPage').then((m: any) => ({ default: m.ComplianceHubPage ?? m.default }))
);
const GapAnalysisPage = lazy(() =>
  import('@/features/compliance/pages/GapAnalysisPage').then((m: any) => ({ default: m.GapAnalysisPage ?? m.default }))
);
const FrameworkDetailPage = lazy(() =>
  import('@/features/compliance/pages/FrameworkDetailPage').then((m: any) => ({ default: m.FrameworkDetailPage ?? m.default }))
);

// Incidents & Vendors
const IncidentDashboardPage = lazy(() =>
  import('@/features/incidents/pages/IncidentDashboardPage').then((m: any) => ({ default: m.IncidentDashboardPage ?? m.default }))
);
const IncidentDetailPage = lazy(() =>
  import('@/features/incidents/pages/IncidentDetailPage').then((m: any) => ({ default: m.IncidentDetailPage ?? m.default }))
);
const VendorRegistryPage = lazy(() =>
  import('@/features/vendors/pages/VendorRegistryPage').then((m: any) => ({ default: m.VendorRegistryPage ?? m.default }))
);
const VendorDetailPage = lazy(() =>
  import('@/features/vendors/pages/VendorDetailPage').then((m: any) => ({ default: m.VendorDetailPage ?? m.default }))
);

// Settings & Onboarding
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((m: any) => ({ default: m.SettingsPage ?? m.default }))
);
const OnboardingWizard = lazy(() =>
  import('@/features/onboarding/pages/OnboardingWizard').then((m) => ({ default: m.OnboardingWizardPage }))
);
const InviteClaimPage = lazy(() => import('@/pages/InviteClaimPage'));

// Lazy imports
const AdminLayout = lazy(() => import('@/features/admin/layouts/AdminLayout'));
const AdminDashboardPage = lazy(() => import('@/features/admin/pages/AdminDashboardPage'));
const TenantListPage = lazy(() => import('@/features/admin/pages/TenantListPage'));
const TenantDetailPage = lazy(() => import('@/features/admin/pages/TenantDetailPage'));
const UserSearchPage = lazy(() => import('@/features/admin/pages/UserSearchPage'));


// ── Offline Banner ───────────────────────────────────
function OfflineBanner() {
  const isOnline = useOnlineStatus();
  if (isOnline) return null;
  return (
    <Alert
      variant="destructive"
      className="rounded-none border-x-0 border-t-0"
      role="alert"
    >
      <WifiOff className="h-4 w-4" aria-hidden="true" />
      <AlertDescription>
        You appear to be offline. Some features may not work until your connection is restored.
      </AlertDescription>
    </Alert>
  );
}

// ── Auth Guard ───────────────────────────────────────
// Uses Clerk's SignedIn / SignedOut components for route protection.
// ── Auth Guard ───────────────────────────────────────
function RequireAuth() {
  const { isSignedIn, isLoaded } = useClerkAuth();

  // Wait for Clerk to finish loading — do NOT redirect yet
  if (!isLoaded) {
    return <FullPageLoader label="Checking authentication..." />;
  }

  // Only redirect once we KNOW the user is not signed in
  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  return <Outlet />;
}

// ── Onboarding Guard ─────────────────────────────────
// If the tenant hasn't completed onboarding, redirect to /onboarding.
// This sits INSIDE RequireAuth but OUTSIDE the main app routes, so the
// user must be logged in but onboarding hasn't finished yet.
function RequireOnboarding() {
  const { tenant, isLoading, error, errorMessage } = useTenant();

  if (isLoading) {
    return <FullPageLoader label="Loading workspace..." />;
  }

  // If we have a tenant, onboarding is complete
  if (tenant) {
    return <Outlet />;
  }

  // If the API errored (e.g. tenant middleware couldn't resolve),
  // show an error screen instead of redirecting to /onboarding in a loop
  if (error) {
    if (errorMessage === "tenant is inactive") {
      return (
        <div className="min-h-screen flex items-center justify-center bg-muted/30">
          <div className="text-center space-y-4 max-w-md p-8 bg-white border rounded-xl shadow-sm">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" aria-hidden="true" />
            </div>
            <h2 className="text-xl font-semibold">Workspace Deactivated</h2>
            <p className="text-muted-foreground">
              Your organization's workspace has been deactivated by the platform administrators.
              Please contact u-Compliance support for assistance.
            </p>
            <div className="pt-4">
              <button
                className="inline-flex items-center justify-center rounded-md bg-secondary px-4 py-2 text-sm font-medium hover:bg-secondary/80 w-full"
                onClick={() => {
                  const outMsg = (window as any).Clerk?.signOut;
                  if (outMsg) outMsg();
                }}
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="text-center space-y-4 max-w-md p-8">
          <h2 className="text-xl font-semibold">Unable to load workspace</h2>
          <p className="text-muted-foreground">
            We couldn't connect to your workspace. This may be a temporary issue.
          </p>
          <button
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No tenant & no error — check if this user has a pending invite to claim
  // before sending them to onboarding. This handles the case where Clerk's
  // post-signup redirect doesn't go through /invite/claim as expected.
  const pendingToken = sessionStorage.getItem('pending_invite_token');
  if (pendingToken) {
    return <Navigate to={`/invite/claim?invite_token=${pendingToken}`} replace />;
  }

  // Genuine first-time user with no invite — redirect to onboarding
  return <Navigate to="/onboarding" replace />;
}

// ── Active Subscription Guard ────────────────────────
function RequireActiveSubscription() {
  const { tenant } = useTenant();

  if (tenant?.plan === 'expired') {
    return <Navigate to="/settings/billing" replace />;
  }

  return <Outlet />;
}

// ── Module Gate ──────────────────────────────────────
// Checks whether the tenant's plan includes the given module.
// If not, shows an UpgradePrompt instead of the child routes.
const MODULE_LABELS: Record<string, { name: string }> = {
  documents: { name: 'Document & Certificate Tracker' },
  checklists: { name: 'Process Checklists' },
  policies: { name: 'Policy Acknowledgement' },
  training: { name: 'Training & Micro-Certification' },
  access: { name: 'Access & Permissions Register' },
  compliance: { name: 'Employee Compliance Records' },
  incidents: { name: 'Incidents & Issues' },
  vendors: { name: 'Vendor Management' },
};

function RequireModule({ module }: { module: string }) {
  const { hasModule } = useTenant();

  if (!hasModule(module)) {
    const meta = MODULE_LABELS[module];
    return (
      <UpgradePrompt
        moduleName={meta?.name ?? module}
      />
    );
  }

  return <Outlet />;
}

// ── App ──────────────────────────────────────────────
export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorBoundary level="page"><></></ErrorBoundary>} showDialog>
      <ErrorBoundary level="page">
        <AuthProvider>
          <TenantProvider>
            <SkipNavigation />
            <OfflineBanner />
            <RouteAnnouncer />

            <Suspense fallback={<FullPageLoader />}>
              <Routes>
                {/* ── Public routes — Clerk hosted components & Legal ── */}
                <Route path="/sign-in/*" element={<SignInPage />} />
                <Route path="/sign-up/*" element={<SignUpPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />

                {/* ── Authenticated routes ──────────── */}
                <Route element={<RequireAuth />}>
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboardPage />} />
                    <Route path="tenants" element={<TenantListPage />} />
                    <Route path="tenants/:id" element={<TenantDetailPage />} />
                    <Route path="users" element={<UserSearchPage />} />
                  </Route>
                  {/* Onboarding — inside auth but OUTSIDE the onboarding guard
                    so we don't get an infinite redirect loop */}
                  <Route path="/onboarding" element={<OnboardingWizard />} />

                  {/* Invite claim — inside auth but OUTSIDE onboarding guard */}
                  <Route path="/invite/claim" element={<InviteClaimPage />} />

                  {/* ── Main app — requires completed onboarding ── */}
                  <Route element={<RequireOnboarding />}>
                    <Route element={<DashboardLayout />}>
                      <Route element={<RequireActiveSubscription />}>
                        <Route index element={<Navigate to="/dashboard" replace />} />
                        <Route path="/dashboard" element={<DashboardPage />} />

                        {/* Module 1: Documents */}
                        <Route path="/documents" element={<RequireModule module="documents" />}>
                          <Route index element={<DocumentListPage />} />
                          <Route path="new" element={<CreateDocumentPage />} />
                          <Route path="dashboard" element={<DocumentDashboardPage />} />
                          <Route path=":id" element={<DocumentDetailPage />} />
                          <Route path=":id/edit" element={<CreateDocumentPage />} />
                          <Route path=":id/renew" element={<RenewDocumentPage />} />
                        </Route>

                        {/* Module 2: Checklists */}
                        <Route path="/checklists" element={<RequireModule module="checklists" />}>
                          <Route index element={<ChecklistListPage />} />
                          <Route path="templates" element={<TemplateListPage />} />
                          <Route path="templates/new" element={<TemplateBuilderPage />} />
                          <Route path="templates/:id/edit" element={<TemplateBuilderPage />} />
                          <Route path="dashboard" element={<ChecklistDashboardPage />} />
                          <Route path="mine" element={<MyChecklistsPage />} />
                          <Route path=":id" element={<ChecklistDetailPage />} />
                        </Route>

                        {/* Module 3: Policies */}
                        <Route path="/policies" element={<RequireModule module="policies" />}>
                          <Route index element={<PolicyListPage />} />
                          <Route path="new" element={<PolicyEditorPage />} />
                          <Route path="compliance" element={<ComplianceDashboardPage />} />
                          <Route path="my-policies" element={<EmployeePoliciesPage />} />
                          <Route path=":id" element={<PolicyReaderPage />} />
                          <Route path=":id/read" element={<PolicyReaderPage />} />
                        </Route>

                        {/* Module 4: Training */}
                        <Route path="/training" element={<RequireModule module="training" />}>
                          <Route index element={<CourseListPage />} />
                          <Route path="new" element={<CourseBuilderPage />} />
                          <Route path="dashboard" element={<TrainingDashboardPage />} />
                          <Route path="my-certifications" element={<MyCertificationsPage />} />
                          <Route path=":id" element={<CourseLearnerPage />} />
                          <Route path=":id/edit" element={<CourseDetailPage />} />
                        </Route>

                        {/* Module 5: Access */}
                        <Route path="/access" element={<RequireModule module="access" />}>
                          <Route index element={<AccessDashboardPage />} />
                          <Route path="systems" element={<SystemRegistryPage />} />
                          <Route path="requests" element={<AccessRequestPage />} />
                          <Route path="offboarding" element={<OffboardingDashboardPage />} />
                          <Route path="roles" element={<RoleTemplateEditorPage />} />
                        </Route>

                        {/* Module 6: Compliance */}
                        <Route path="/compliance" element={<RequireModule module="compliance" />}>
                          <Route index element={<ComplianceHubPage />} />
                          <Route path="employees" element={<EmployeeListPage />} />
                          <Route path="employees/:id" element={<EmployeeProfilePage />} />
                          <Route path="gaps" element={<GapAnalysisPage />} />
                          <Route path="item-types" element={<ComplianceItemTypesPage />} />
                          <Route path="frameworks/:id" element={<FrameworkDetailPage />} />
                        </Route>

                        {/* Incidents Module */}
                        <Route path="/incidents" element={<RequireModule module="incidents" />}>
                          <Route index element={<IncidentDashboardPage />} />
                          <Route path=":id" element={<IncidentDetailPage />} />
                        </Route>

                        {/* Vendors Module */}
                        <Route path="/vendors" element={<RequireModule module="vendors" />}>
                          <Route index element={<VendorRegistryPage />} />
                          <Route path=":id" element={<VendorDetailPage />} />
                        </Route>
                      </Route>

                      {/* Settings & Billing */}
                      <Route path="/settings" element={<SettingsPage />} />
                      <Route path="/settings/billing" element={<SettingsPage />} />
                    </Route>
                  </Route>
                </Route>

                {/* ── Catch-all ────────────────────── */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>

            <Toaster richColors position="top-right" />
          </TenantProvider>
        </AuthProvider>
      </ErrorBoundary>
    </Sentry.ErrorBoundary>
  );
}